/**
 * @name HistoricoPage
 * @author Gabriel M. Guimarães | gabrielmg7
 * @description Página de histórico de transações - consumindo vw_extrato_movimentacoes_aluno via hook
 * @package components/pages/gestao-financeira/historico
 */

import { GetApp as ExportIcon, History as HistoryIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { Box, Button, Card, CardContent, Chip, CircularProgress, Grid, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import React from 'react';
import PageContainer from '../../../layouts/PageContainer';
import useHistoricoData from './useHistoricoData';

/**
 * Página de histórico de transações
 * Consome dados da view vw_extrato_movimentacoes_aluno via useHistoricoData
 */
const HistoricoPage: React.FC = () => {
     // Hook com dados reais
     const { movimentacoes, loading, filtros, metricas, updateFiltros, recarregar, exportarDados, metadados } = useHistoricoData();

     // Ações do cabeçalho
     const actions = (
          <>
               <Button
                    variant='outlined'
                    startIcon={<RefreshIcon />}
                    onClick={recarregar}
                    disabled={loading}
               >
                    Atualizar
               </Button>
               <Button
                    variant='contained'
                    startIcon={<ExportIcon />}
                    onClick={exportarDados}
                    disabled={loading || movimentacoes.length === 0}
                    color='secondary'
               >
                    Exportar
               </Button>
          </>
     );

     const getTipoColor = (tipo: string | null): 'success' | 'error' | 'default' => {
          if (tipo === 'credito') return 'success';
          if (tipo === 'debito') return 'error';
          return 'default';
     };

     const formatCurrency = (value: number | null): string => {
          if (value === null || value === undefined) return 'R$ 0,00';
          return new Intl.NumberFormat('pt-BR', {
               style: 'currency',
               currency: 'BRL',
          }).format(value);
     };

     const formatDate = (dateString: string | null): string => {
          if (!dateString) return '-';
          return new Date(dateString).toLocaleDateString('pt-BR');
     };

     const formatTipoMovimentacao = (tipo: string | null): string => {
          if (tipo === 'credito') return 'Crédito';
          if (tipo === 'debito') return 'Débito';
          return 'N/A';
     };

     return (
          <PageContainer
               title={metadados.titulo}
               breadcrumbs={metadados.breadcrumbs}
               actions={actions}
               icon={<HistoryIcon />}
          >
               <Grid
                    container
                    spacing={3}
               >
                    {/* Filtros */}
                    <Grid
                         item
                         xs={12}
                    >
                         <Card>
                              <CardContent>
                                   <Typography
                                        variant='h6'
                                        gutterBottom
                                   >
                                        Filtros
                                   </Typography>
                                   <Grid
                                        container
                                        spacing={2}
                                   >
                                        <Grid
                                             item
                                             xs={12}
                                             md={4}
                                        >
                                             <TextField
                                                  select
                                                  label='Tipo de Movimentação'
                                                  value={filtros.tipo}
                                                  onChange={(e) => updateFiltros({ tipo: e.target.value as any })}
                                                  fullWidth
                                                  size='small'
                                             >
                                                  <MenuItem value='todos'>Todos</MenuItem>
                                                  <MenuItem value='credito'>Créditos</MenuItem>
                                                  <MenuItem value='debito'>Débitos</MenuItem>
                                             </TextField>
                                        </Grid>
                                        <Grid
                                             item
                                             xs={12}
                                             md={4}
                                        >
                                             <TextField
                                                  select
                                                  label='Período'
                                                  value={filtros.periodo}
                                                  onChange={(e) => updateFiltros({ periodo: e.target.value as any })}
                                                  fullWidth
                                                  size='small'
                                             >
                                                  <MenuItem value='ultimos_7_dias'>Últimos 7 dias</MenuItem>
                                                  <MenuItem value='ultimos_30_dias'>Últimos 30 dias</MenuItem>
                                                  <MenuItem value='ultimos_90_dias'>Últimos 90 dias</MenuItem>
                                                  <MenuItem value='este_ano'>Este ano</MenuItem>
                                             </TextField>
                                        </Grid>
                                        <Grid
                                             item
                                             xs={12}
                                             md={4}
                                        >
                                             <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                                                  <Typography
                                                       variant='body2'
                                                       color='text.secondary'
                                                  >
                                                       {metricas.totalMovimentacoesFiltradas} de {metricas.totalMovimentacoes} movimentações
                                                  </Typography>
                                             </Box>
                                        </Grid>
                                   </Grid>
                              </CardContent>
                         </Card>
                    </Grid>

                    {/* Tabela de Movimentações */}
                    <Grid
                         item
                         xs={12}
                    >
                         <Card>
                              <CardContent>
                                   <Typography
                                        variant='h6'
                                        gutterBottom
                                   >
                                        Movimentações
                                        {loading && (
                                             <CircularProgress
                                                  size={16}
                                                  sx={{ ml: 1 }}
                                             />
                                        )}
                                   </Typography>

                                   {loading ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                             <CircularProgress />
                                        </Box>
                                   ) : movimentacoes.length === 0 ? (
                                        <Box sx={{ textAlign: 'center', p: 4 }}>
                                             <Typography
                                                  variant='body2'
                                                  color='text.secondary'
                                             >
                                                  Nenhuma movimentação encontrada para o período selecionado.
                                             </Typography>
                                        </Box>
                                   ) : (
                                        <TableContainer
                                             component={Paper}
                                             variant='outlined'
                                        >
                                             <Table>
                                                  <TableHead>
                                                       <TableRow>
                                                            <TableCell>Data</TableCell>
                                                            <TableCell>Pessoa</TableCell>
                                                            <TableCell>Descrição</TableCell>
                                                            <TableCell>Tipo</TableCell>
                                                            <TableCell align='right'>Valor</TableCell>
                                                            <TableCell align='right'>Saldo Anterior</TableCell>
                                                            <TableCell align='right'>Saldo Posterior</TableCell>
                                                       </TableRow>
                                                  </TableHead>
                                                  <TableBody>
                                                       {movimentacoes.map((movimentacao) => (
                                                            <TableRow key={movimentacao.id}>
                                                                 <TableCell>{formatDate(movimentacao.data_movimentacao)}</TableCell>
                                                                 <TableCell>{movimentacao.pessoa || '-'}</TableCell>
                                                                 <TableCell>{movimentacao.descricao || '-'}</TableCell>
                                                                 <TableCell>
                                                                      <Chip
                                                                           label={formatTipoMovimentacao(movimentacao.tipo_movimentacao)}
                                                                           color={getTipoColor(movimentacao.tipo_movimentacao)}
                                                                           size='small'
                                                                      />
                                                                 </TableCell>
                                                                 <TableCell align='right'>{formatCurrency(movimentacao.valor)}</TableCell>
                                                                 <TableCell align='right'>{formatCurrency(movimentacao.saldo_anterior)}</TableCell>
                                                                 <TableCell align='right'>{formatCurrency(movimentacao.saldo_posterior)}</TableCell>
                                                            </TableRow>
                                                       ))}
                                                  </TableBody>
                                             </Table>
                                        </TableContainer>
                                   )}
                              </CardContent>
                         </Card>
                    </Grid>
               </Grid>
          </PageContainer>
     );
};

export default HistoricoPage;
